/**
 * PlaybookRenameModal Component
 *
 * Simple modal for renaming an existing playbook.
 * Pre-fills with current name, validates non-empty.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services & Types
import { playbookService } from '@/services/PlaybookService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { Playbook } from '@/types/schema/Playbook';

/**
 * Props for PlaybookRenameModal
 */
export interface PlaybookRenameModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Playbook to rename */
  playbook: Playbook;
  /** Callback after successful save */
  onSave: () => void;
}

/**
 * PlaybookRenameModal Component
 */
export const PlaybookRenameModal: React.FC<PlaybookRenameModalProps> = ({
  isOpen,
  onClose,
  playbook,
  onSave,
}) => {
  // Form state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Reset form when modal opens or playbook changes
  useEffect(() => {
    if (isOpen) {
      setName(playbook.name);
      setNameError(null);
      setSubmitError(null);
    }
  }, [isOpen, playbook.name]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  /**
   * Handle name change
   */
  const handleNameChange = (value: string) => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();

    // Skip if name unchanged
    if (trimmedName === playbook.name) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);

      await playbookService.update(playbook.id, { name: trimmedName });
      syncCoordinatorService.notifyChange('playbooks', playbook.id);

      showToast({
        title: 'Playbook Renamed',
        description: `Playbook renamed to "${trimmedName}"`,
        color: 'success',
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to rename playbook:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename playbook';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="top-center"
    >
      <ModalContent>
        <ModalHeader>Rename Playbook</ModalHeader>

        <ModalBody className="gap-4">
          {/* Name */}
          <Input
            label="Name"
            placeholder="Enter playbook name"
            value={name}
            onValueChange={handleNameChange}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PlaybookRenameModal;
